import { Icon } from "@iconify/react";
import jwtDecode from "jwt-decode";
import * as React from "react";
import { ButtonHTMLAttributes } from "react";
import { toast } from "react-toastify";
import {
  Dialog,
  Description,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useAuthStore, useCompanyStore } from "../store";
import { usePermissionStore } from "../store/permissionStore";
import { Action, Resource } from "../utils/interfaces/Companies";
import { ValidationResponse } from "../types/Employee";
import { StrikePriceType } from "../types/EsopPlan";
import { Input, Label } from "./shared/InputField";
import { useExportToExcel, useExportToExcelManager } from "../queries";
import { convertBase64ToBlob, downloadBlobObject } from "../utils/DownloadFile";
import { SwitchButton } from "./shared/SwitchButton";
import { MultiSelectV2 } from "./shared/MultiSelectV2";
import { Select } from "./shared/Select";
import Tooltip from "./shared/Tooltip";
import { useReportPiiMaskDefault } from "../queries/siteSettings";
/*
Wrapper components around regular divs to improve code readability
with nested divs
*/

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {}
type SecondaryCTAButtonProps = {
  buttonSize?: ButtonSize;
  className?: string;
  loadingText?: string;
  loading?: boolean;
} & ButtonProps;
type PrimaryCTAButtonProps = {
  buttonSize?: ButtonSize;
  className?: string;
  loadingText?: string;
  loading?: boolean;
} & ButtonProps;

type SettingsRowProps = {
  label: string;
  description?: string;
  type: "toggle" | "input" | "multiselect" | "select";
  inputType?: "text" | "email" | "number" | "date";
  value: boolean | string | number | any[];
  name: string;
  id?: string;
  onToggle?: () => void;
  onChange?: (values: any[]) => void;
  textGetter?: any;
  valueGetter?: any;
  options?: any[];
  getFieldProps?: any;
  error?: string;
  disabled?: boolean;
  tooltip?: string;
  required?: boolean;
};
export interface TableDataExportProps {
  headers?: { key: string; value: any }[];
  data?: any[];
  onDiscard?: () => void;
  tableName?: string;
  heading?: string;
  fileName?: string;
  open?: boolean;
  /** Use the manager-scoped export endpoint for manager portal data. */
  isManagerPortal?: boolean;
}

export enum ButtonSize {
  SMALL = "px-2 py-1.5 text-xs1 leading-[18px]",
  MEDIUM = "px-3 py-2.5 text-xs2 leading-4",
}
export function HStack(
  props: React.HTMLAttributes<HTMLDivElement>
): React.ReactElement {
  const className = `flex flex-row ${props.className}`;
  return <div {...props} className={className}></div>;
}

export function VStack(
  props: React.HTMLAttributes<HTMLDivElement>
): React.ReactElement {
  const className = `flex flex-col ${props.className}`;
  return <div {...props} className={className}></div>;
}

export function Center(
  props: React.HTMLAttributes<HTMLDivElement>
): React.ReactElement {
  const className = `flex items-center justify-center ${props.className}`;
  return <div {...props} className={className}></div>;
}

export function Box(
  props: React.HTMLAttributes<HTMLDivElement>
): React.ReactElement {
  const className = `${props.className}`;
  return <div {...props} className={className}></div>;
}

export function ButtonPrimary({
  className,
  loadingText,
  loading = false,
  type = "button",
  buttonSize = ButtonSize.SMALL,
  disabled = false,
  children,
  ...rest
}: PrimaryCTAButtonProps): React.ReactElement {
  const buttonClassName = `inline-flex items-center text-center ${buttonSize} rounded-sm border border-transparent hover:shadow-md  whitespace-nowrap font-semibold text-white shadow-box  focus:outline-none ${
    disabled
      ? "bg-gray-300 cursor-not-allowed"
      : `${className} bg-primary hover:bg-primaryHover hover:bg-primary/70 cursor-pointer`
  }`;
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={buttonClassName}
      type={type}
    >
      {loading ? (
        <LoaderText buttonSize={buttonSize} text={loadingText} />
      ) : (
        children
      )}
    </button>
  );
}
export function LoaderText({
  buttonSize,
  text,
}: {
  buttonSize?: ButtonSize;
  text?: string;
}) {
  const loadText = text ?? "Please wait...";
  return (
    <HStack className="cursor-wait">
      <p
        className="indicator-progress text-center pt-0.5"
        style={{ display: "block" }}
      >
        <span className=""> {loadText}</span>
      </p>
      <Icon
        className="animate-spin"
        icon="lucide:loader-2"
        width={buttonSize === ButtonSize.SMALL ? 16 : 14}
      />
    </HStack>
  );
}

export function ButtonPrimary1({
  className,
  loadingText,
  loading = false,
  disabled = false,
  type = "button",
  buttonSize = ButtonSize.SMALL,
  children,
  ...rest
}: SecondaryCTAButtonProps): React.ReactElement {
  const buttonClassName = `inline-flex bg-transparent  items-center ${buttonSize} rounded-sm border border-primary whitespace-nowrap font-semibold text-primary drop-shadow-box hover:bg-primary/20 focus:outline-none ${className} ${
    disabled || loading
      ? "cursor-not-allowed bg-gray-300 hover:bg-gray-300"
      : "bg-orange-p hover:bg-orange-500 cursor-pointer"
  }`;

  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={buttonClassName}
      type={type}
    >
      {loading ? (
        <LoaderText buttonSize={buttonSize} text={loadingText} />
      ) : (
        children
      )}
    </button>
  );
}

export function ButtonSecondary({
  className,
  type = "button",
  disabled = false,
  buttonSize = ButtonSize.SMALL,
  ...rest
}: PrimaryCTAButtonProps): React.ReactElement {
  const _type = type || "button";
  const _className = `inline-flex items-center border border-[#F9F9F9] ${buttonSize} rounded-md border border-transparent bg-tertiaryBg whitespace-nowrap font-semibold text-tertiaryText drop-shadow-box hover:bg-tertiaryBgHover focus:outline-none ${className} ${
    disabled
      ? "cursor-not-allowed bg-gray-300 hover:bg-gray-300"
      : "bg-orange-p hover:bg-orange-500 cursor-pointer"
  } `;
  return <button {...rest} className={_className} type={_type}></button>;
}

export function ButtonCancelSecondary(
  props: React.ButtonHTMLAttributes<HTMLButtonElement>
): React.ReactElement {
  const { className, type, ...rest } = props;
  const _type = type || "button";
  const _className = `p-2 font-normal rounded h-8 text-gray-dark text-sm1 bg-gray-light focus:opacity-90 hover:bg-gray-100 ${className}`;
  return <button {...rest} className={_className} type={_type}></button>;
}

export function Error({ text }: { text?: string }) {
  return <p className="text-[9px] text-red-500 my-0.5">{text}</p>;
}

function getAccessToken(): string {
  const token = useAuthStore.getState().accessToken || "";
  return token;
}
export function redirectToHissa(path: string, postLogout = false) {
  const companyData = useCompanyStore.getState().companyData;
  const isLocal = window.location.hostname === "localhost";
  if (isLocal) return;
  const hissaUrl =
    process.env.REACT_APP_LEGACY_URL ||
    sessionStorage.getItem("hissaUrl") ||
    "https://dev.hissa.com";
  if (postLogout) {
    window.location.href = hissaUrl;
    return;
  }
  window.location.href = `${hissaUrl}/${path}?accessToken=${getAccessToken()}&companyId=${
    companyData?.id
  }`;
}

export function clearCredentials() {
  const credentialKeys = [
    "accesstoken",
    "hissaUrl",
    "companyId",
    "x-usr-token",
  ];
  credentialKeys.forEach((key) => {
    sessionStorage.removeItem(key);
  });
}

export function decodeAuthToken(accesstoken: string) {
  const decodedToken = jwtDecode(accesstoken.replace(/^Bearer\s+/i, "")) as {
    xUserToken: string;
    exp: number;
    id: string;
    emailId: string;
    type?: string;
  };
  return decodedToken;
}

export function checkTokenValidity(clockSkew = 30000) {
  const CLOCK_SKEW_MARGIN = 1000 * 60 * 5; // 5 minutes
  const token = useAuthStore.getState().accessToken;
  if (!token) return true;
  const decodedToken = decodeAuthToken(token);
  const currentDate = new Date();
  if (decodedToken.exp * 1000 - currentDate.getTime() < clockSkew) return false;
  return true;
}

export function revalidateAuth() {
  if (!checkTokenValidity()) {
    useAuthStore.setState((store) => ({ ...store, isAuthenticated: false }));
  }
}

export function getCompanyName() {
  const { companyData } = useCompanyStore();
  return companyData?.name;
}

export function isAdminViewer() {
  const { permission } = usePermissionStore();
  const isUserAdminViewer =
    permission?.aclList.length === 1 &&
    permission?.aclList[0].action === Action.Read &&
    permission?.aclList[0].resource === Resource.All;
  return isUserAdminViewer;
}

export function isCompanySettingsReadOnly() {
  const { permission } = usePermissionStore();
  return (
    (permission?.aclList.length === 1 &&
      permission?.aclList[0].action === Action.Read &&
      permission?.aclList[0].resource === Resource.All) ||
    permission?.role === "CHECKER"
  );
}

export function isAdmin() {
  const { permission } = usePermissionStore();
  const isUserAdmin =
    permission?.aclList.length === 1 &&
    permission?.aclList[0].action === Action.All &&
    permission?.aclList[0].resource === Resource.All;
  return isUserAdmin;
}

export function isEsopViewer() {
  const { permission } = usePermissionStore();
  const isEsopViewer =
    permission?.aclList.length === 1 &&
    permission?.aclList[0].action === Action.Read &&
    permission?.aclList[0].resource === Resource.All;
  return isEsopViewer;
}

export function isManager() {
  const { permission } = usePermissionStore();
  return permission?.role === "MANAGER";
}

export function SecondaryCTAButton({
  className,
  type = "button",
  buttonSize = ButtonSize.MEDIUM,
  ...rest
}: SecondaryCTAButtonProps): React.ReactElement {
  const buttonClassName = `inline-flex bg-transparent items-center cursor-pointer ${buttonSize} rounded-sm border border-primary  whitespace-nowrap font-semibold text-primary drop-shadow-box hover:bg-primary/20 focus:outline-none ${className}`;
  return <button {...rest} className={buttonClassName} type={type} />;
}

export function PrimaryCTAButton({
  className,
  type = "button",
  buttonSize = ButtonSize.MEDIUM,
  loadingText,
  loading = false,
  disabled = false,
  children,
  ...rest
}: PrimaryCTAButtonProps): React.ReactElement {
  const buttonClassName = `inline-flex items-center ${buttonSize} rounded-sm border border-transparent whitespace-nowrap font-semibold text-white drop-shadow-box focus:outline-none ${
    disabled || loading
      ? "!bg-gray-300 hover:!bg-gray-300 cursor-not-allowed"
      : "cursor-pointer bg-primary hover:bg-primaryHover hover:bg-primary/70"
  } ${className || ""}`;
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={buttonClassName}
      type={type}
    >
      {loading ? (
        <LoaderText buttonSize={buttonSize} text={loadingText} />
      ) : (
        children
      )}
    </button>
  );
}
export function TertiaryCTAButton({
  className,
  type = "button",
  buttonSize = ButtonSize.MEDIUM,
  ...rest
}: PrimaryCTAButtonProps): React.ReactElement {
  const buttonClassName = `inline-flex items-center border border-[#F9F9F9] cursor-pointer ${buttonSize} rounded-md border border-transparent bg-tertiaryBg whitespace-nowrap font-semibold text-tertiaryText drop-shadow-box hover:bg-tertiaryBgHover focus:outline-none ${className}`;
  return <button {...rest} className={buttonClassName} type={type} />;
}
export enum IconSize {
  S = 12,
  M = 18,
  L = 24,
}
export enum IconPlacement {
  LEFT,
  RIGHT,
}
export enum IconButtonType {
  FILLED = "bg-orange-501 text-white shadow-sm font-semibold",
  OUTLINED = "font-semibold",
  NONE = "font-medium",
}
type IconCTAButtonProps = {
  value?: string;
  children?: React.ReactNode;
  buttonSize?: ButtonSize;
  selected?: boolean;
  iconOnly?: boolean;
  destructive?: boolean;
  iconName?: string;
  buttonType?: IconButtonType;
  className?: string;
  iconClassName?: string;
  iconSize?: IconSize;
  iconPlacement?: IconPlacement;
} & ButtonProps;
export function IconCTAButton(props: IconCTAButtonProps) {
  const {
    value,
    children,
    selected = false,
    iconOnly = false,
    destructive = false,
    iconName,
    className,
    buttonType = IconButtonType.OUTLINED,
    buttonSize = ButtonSize.MEDIUM,
    iconClassName,
    iconSize = IconSize.M,
    iconPlacement = IconPlacement.LEFT,
    ...rest
  } = props;
  const placementClass =
    iconPlacement === IconPlacement.RIGHT ? "flex-row-reverse" : "";
  const selectedStyle = destructive
    ? "text-red-600 bg-transparent hover:bg-red-50"
    : selected
    ? "text-white bg-secondary"
    : "text-secondary bg-transparent";
  const buttonClassName = `${buttonType} inline-flex border border-borderColor items-center gap-x-1 rounded-sm ${
    iconOnly ? "h-10 w-10 justify-center p-0" : buttonSize
  } font-medium ${className} ${placementClass} ${
    buttonType === IconButtonType.OUTLINED ? selectedStyle : "border-none"
  }`;
  return (
    <button {...rest} className={buttonClassName} type={"button"}>
      {iconName && (
        <Icon
          aria-hidden="true"
          icon={iconName}
          className={iconClassName}
          height={iconSize}
          width={iconSize}
        />
      )}
      {children ?? value}
    </button>
  );
}
type LoaderButtonProps = {
  buttonSize?: ButtonSize;
  className?: string;
} & ButtonProps;
export function LoaderCTAButton({
  className,
  type = "button",
  buttonSize = ButtonSize.MEDIUM,
  ...rest
}: LoaderButtonProps): React.ReactElement {
  const _className = `inline-flex items-center cursor-not-allowed ${buttonSize} rounded-sm border border-transparent bg-primary px-3 py-2.5 text-sm font-medium leading-4 text-white drop-shadow-box hover:bg-primary/70 ${className}`;
  return (
    <button {...rest} className={_className} type={type} disabled>
      <HStack>
        <p className="indicator-progress" style={{ display: "block" }}>
          Please wait
        </p>
        <Icon className="ml-2 animate-spin" icon="lucide:loader-2" width={18} />
      </HStack>
    </button>
  );
}
//flag
export const CellRenderer = (props: ValidationResponse) => {
  if (!props.valid) {
    return (
      <>
        <p>
          <span>{props.value ?? ""}</span>
        </p>
        <VStack className="gap-0 mt-3.5">
          {props.error?.split("\n").map((line, index) => (
            <div key={index} className="leading-none py-0">
              <span className="italic text-xxs">* {line}</span>
            </div>
          ))}
        </VStack>
      </>
    );
  }
  if (props.warning) {
    return <span title={props.warning}>⚠ {props.value}</span>;
  }
  return <span>{props.value}</span>;
};
export const ValidityCellRenderer = (props: ValidationResponse) => {
  if (!props.valid) {
    return (
      <>
        <p>
          <span>{props.value}</span>
        </p>
        <VStack className="gap-0">
          {props.error?.split("\n").map((line, index) => (
            <p key={index} className="m-0">
              <span className="italic text-xxs">* {line}</span>
            </p>
          ))}
        </VStack>
      </>
    );
  }
  if (props.warning) {
    return <span className="text-amber-600">⚠ {props.value}</span>;
  }
  return <span className="text-green-400">{props.value}</span>;
};
export const ExportTableData = (props: TableDataExportProps) => {
  const { companyData } = useCompanyStore();
  const {
    headers = [],
    data,
    onDiscard,
    tableName,
    heading,
    fileName,
    isManagerPortal = false,
  } = props;
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedHeaders, setSelectedHeaders] = React.useState<Set<string>>(
    new Set(headers.map((header) => header.key))
  );
  const [selectedHeaderOrder, setSelectedHeaderOrder] = React.useState<
    string[]
  >(headers.map((header) => header.key));
  const [draggedHeader, setDraggedHeader] = React.useState<string | null>(null);

  React.useEffect(() => {
    setSelectedHeaders(new Set(headers.map((header) => header.key)));
    setSelectedHeaderOrder(headers.map((header) => header.key));
    setSearchTerm("");
  }, [headers]);

  const toggleHeaderSelection = (key: string) => {
    setSelectedHeaders((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
        setSelectedHeaderOrder((order) =>
          order.filter((headerKey) => headerKey !== key)
        );
      } else {
        next.add(key);
        setSelectedHeaderOrder((order) => [...order, key]);
      }
      return next;
    });
  };

  const moveSelectedHeader = (targetKey: string) => {
    if (!draggedHeader || draggedHeader === targetKey) return;
    setSelectedHeaderOrder((order) => {
      const next = order.filter((key) => key !== draggedHeader);
      const targetIndex = next.indexOf(targetKey);
      next.splice(targetIndex, 0, draggedHeader);
      return next;
    });
  };

  const availableHeaders = headers.filter(
    (header) =>
      !selectedHeaders.has(header.key) &&
      String(header.value).toLowerCase().includes(searchTerm.toLowerCase())
  );
  const selectedHeaderList = selectedHeaderOrder
    .map((key) => headers.find((header) => header.key === key))
    .filter((header): header is { key: string; value: any } => !!header);

  const getFilteredHeaders = () =>
    selectedHeaderOrder
      .map((key) => headers.find((header) => header.key === key))
      .filter((header): header is { key: string; value: any } => !!header);
  const { maskPiiData, setMaskPiiData } = useReportPiiMaskDefault();
  const { mutate: exportExcel } = useExportToExcel();
  const { mutate: exportExcelManager } = useExportToExcelManager();
  const [isDownloading, setIsDownloading] = React.useState(false);
  const download = () => {
    const filteredHeaders = getFilteredHeaders();
    if (filteredHeaders.length === 0) {
      toast("Please select at least one column", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }
    setIsDownloading(true);
    const reqObj = {
      heading,
      companyName: companyData?.name,
      data,
      headers: filteredHeaders,
      maskPiiData,
    };

    const exportMutation = isManagerPortal ? exportExcelManager : exportExcel;
    exportMutation(reqObj, {
      onSuccess: async (data) => {
        const reportDto = data.data;
        const blob = await convertBase64ToBlob(
          reportDto.base64File ?? "",
          reportDto.fileType
            ? reportDto.fileType
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        );
        downloadBlobObject(blob, fileName || "");
        setIsDownloading(false);
        if (onDiscard) onDiscard();
      },
      onError: () => {
        setIsDownloading(false);
        toast("Something Went Wrong", { type: "error", autoClose: 2000 });
      },
    });
  };
  return (
    <Box className="w-[min(92vw,760px)] bg-white text-primaryText">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <h6 className="text-lg font-semibold text-gray-800">
          Download {tableName} data
        </h6>
        <button
          type="button"
          aria-label="Close export dialog"
          onClick={onDiscard}
          className="rounded-full p-1 text-gray-401 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <Icon icon="mdi:close" width={20} />
        </button>
      </div>

      <div className="px-6 py-5">
        <p className="mb-4 text-xs2 text-secondaryText">
          Select and organize the columns you want to include in your export.
        </p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <section className="flex h-[315px] flex-col overflow-hidden rounded-md border border-inputBorder bg-white">
            <div className="flex h-10 shrink-0 items-center justify-between border-b border-borderColor bg-gray-50 px-3">
              <h6 className="text-xs font-semibold text-gray-700">
                Available Columns
              </h6>
              <button
                type="button"
                disabled={selectedHeaders.size === headers.length}
                onClick={() => {
                  setSelectedHeaders(
                    new Set(headers.map((header) => header.key))
                  );
                  setSelectedHeaderOrder(headers.map((header) => header.key));
                }}
                className="text-[11px] font-medium text-primary disabled:cursor-not-allowed disabled:opacity-40"
              >
                Add All
              </button>
            </div>
            <div className="border-b border-borderColor p-2">
              <div className="flex h-8 items-center gap-2 rounded border border-inputBorder bg-white px-2 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20">
                <Icon
                  icon="lucide:search"
                  width={14}
                  className="text-descriptionColor"
                />
                <input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search columns..."
                  aria-label="Search available columns"
                  className="min-w-0 flex-1 border-0 bg-transparent text-xs outline-none placeholder:text-descriptionColor"
                />
                {searchTerm && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => setSearchTerm("")}
                    className="text-descriptionColor hover:text-primaryText"
                  >
                    <Icon icon="lucide:x" width={13} />
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {availableHeaders.length ? (
                availableHeaders.map((header) => (
                  <div
                    key={header.key}
                    className="group flex min-h-[27px] w-full items-center justify-between px-3 py-1 text-left text-[11px] hover:bg-gray-50"
                  >
                    <span className="truncate">{header.value}</span>
                    <button
                      type="button"
                      aria-label={`Add ${String(header.value)}`}
                      onClick={() => toggleHeaderSelection(header.key)}
                      className="shrink-0 rounded p-0.5 text-primary opacity-0 hover:bg-gray-200 focus:opacity-100 focus:outline-none group-hover:opacity-100"
                    >
                      <Icon icon="lucide:plus" width={14} />
                    </button>
                  </div>
                ))
              ) : (
                <p className="px-3 py-5 text-center text-xs text-descriptionColor">
                  {searchTerm
                    ? "No matching columns"
                    : "All columns are selected"}
                </p>
              )}
            </div>
          </section>

          <section className="flex h-[315px] flex-col overflow-hidden rounded-md border border-inputBorder bg-white">
            <div className="flex h-10 shrink-0 items-center justify-between border-b border-borderColor bg-gray-50 px-3">
              <h6 className="text-xs font-semibold text-gray-700">
                Selected Columns ({selectedHeaderList.length})
              </h6>
              <button
                type="button"
                disabled={!selectedHeaders.size}
                onClick={() => {
                  setSelectedHeaders(new Set());
                  setSelectedHeaderOrder([]);
                }}
                className="text-[11px] font-medium text-primary disabled:cursor-not-allowed disabled:opacity-40"
              >
                Clear All
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {selectedHeaderList.length ? (
                selectedHeaderList.map((header) => (
                  <div
                    key={header.key}
                    draggable
                    onDragStart={(event) => {
                      setDraggedHeader(header.key);
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", header.key);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = "move";
                    }}
                    onDragEnter={() => moveSelectedHeader(header.key)}
                    onDragEnd={() => setDraggedHeader(null)}
                    className={`group flex min-h-[27px] w-full cursor-grab items-center gap-2 px-3 py-1 text-left text-[11px] hover:bg-gray-50 active:cursor-grabbing ${
                      draggedHeader === header.key
                        ? "bg-gray-100 opacity-60"
                        : ""
                    }`}
                    title="Drag to reorder"
                  >
                    <Icon
                      icon="lucide:grip-vertical"
                      width={13}
                      className="shrink-0 text-gray-400"
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {header.value}
                    </span>
                    <button
                      type="button"
                      draggable={false}
                      aria-label={`Remove ${String(header.value)}`}
                      onClick={() => toggleHeaderSelection(header.key)}
                      className="shrink-0 rounded p-0.5 text-primary opacity-0 hover:bg-gray-200 focus:opacity-100 focus:outline-none group-hover:opacity-100"
                    >
                      <Icon icon="lucide:x" width={13} />
                    </button>
                  </div>
                ))
              ) : (
                <p className="px-3 py-5 text-center text-xs text-descriptionColor">
                  Select columns from the available list
                </p>
              )}
            </div>
          </section>
        </div>
      </div>

      <div className="border-t border-gray-100 px-6 py-4">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <p className="text-xs font-medium">Security Settings</p>
            <p className="mt-1 text-xs text-secondaryText">
              Manage data privacy for your export
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={maskPiiData}
            onClick={() => setMaskPiiData((value) => !value)}
            className="group flex items-center gap-2 text-left text-[11px] focus:outline-none"
          >
            <span
              className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ring-offset-2 group-focus:ring-2 group-focus:ring-primary/30 ${
                maskPiiData ? "bg-primary" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                  maskPiiData ? "translate-x-[18px]" : "translate-x-0.5"
                }`}
              />
            </span>
            Hide all Personally Identifiable Information (PII)
          </button>
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4">
        <ButtonSecondary onClick={onDiscard}>Discard</ButtonSecondary>
        <ButtonPrimary
          loading={isDownloading}
          disabled={!selectedHeaders.size}
          onClick={download}
        >
          Download
        </ButtonPrimary>
      </div>
    </Box>
  );
};
interface ExportImportButtonProps {
  actions: Action[];
  onAction: (action: Action) => void;
}

export function formatToTitleCase(str: string) {
  return str
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function getGrantStrikePrice(
  strikePrice: number,
  planConversionRatio: number,
  strikePriceType: string
) {
  if (strikePriceType === StrikePriceType.SHARE_PRICE) {
    return strikePrice * planConversionRatio;
  } else {
    return strikePrice;
  }
}

export function WarningModal({ open }: { open: boolean }) {
  return (
    <Dialog as="div" className="relative z-50" open={open} onClose={() => {}}>
      <div
        className="fixed inset-0 bg-black bg-opacity-30"
        aria-hidden="true"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-sm p-6 bg-white rounded-lg shadow-xl">
          <VStack className="space-y-4">
            <DialogTitle className="text-lg font-bold text-danger">
              Warning!!!
            </DialogTitle>
            <div className="border-b border-gray-300"></div>
            <Description>
              You have already logged into this Platform on another browser tab.
              Kindly revert to the original login tab or close the other tab to
              proceed.
            </Description>
          </VStack>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export const Spinner = ({
  size = 40,
  className = "",
  width = "50%",
  height = 20,
  count = 10,
}: {
  size?: number;
  className?: string;
  width?: string | number;
  height?: string | number;
  count?: number;
}) => (
  <VStack className={`w-full h-full items-center justify-center ${className}`}>
    {[...Array(count)].map((_, idx) => (
      <HStack key={idx} className="w-full gap-4 mb-4">
        <VStack className="w-full gap-2">
          <div
            className="mb-2 bg-gray-200 rounded-md animate-pulse"
            style={{
              width: typeof width === "number" ? `${width}px` : width,
              height: typeof height === "number" ? `${height}px` : height,
            }}
          ></div>
          <HStack className="w-full gap-2">
            <div
              className="bg-gray-200 rounded-md animate-pulse"
              style={{
                width: "60%",
                height: 16,
              }}
            ></div>
            <div
              className="bg-gray-200 rounded-md animate-pulse"
              style={{
                width: "30%",
                height: 16,
              }}
            ></div>
          </HStack>
        </VStack>
      </HStack>
    ))}
  </VStack>
);

export function toInputDate(date: string | Date) {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

export const SettingsRow = (props: SettingsRowProps) => {
  const {
    label,
    description,
    type,
    value,
    onToggle,
    onChange,
    options,
    getFieldProps,
    error,
    disabled,
    inputType = "text",
    name,
    id,
    textGetter,
    valueGetter,
    tooltip,
    required,
  } = props;

  return (
    <HStack id={id} className="w-full justify-between items-center">
      <VStack className="flex-1">
        <HStack className="gap-1">
          <span className={`font-medium ${required ? "required" : ""}`}>
            {label}
          </span>
          {tooltip && (
            <Tooltip text={tooltip} _className="w-[250px]">
              <Icon
                icon="material-symbols:info-outline-rounded"
                height={18}
                width={18}
                className="w-full text-xs font-medium rounded cursor-pointer text-slate-dark"
              />
            </Tooltip>
          )}
        </HStack>
        {description && (
          <span className="text-xs text-gray-500">{description}</span>
        )}
      </VStack>
      <VStack className="justify-end min-w-44 max-w-60 items-end">
        {type === "toggle" ? (
          <SwitchButton
            value={Boolean(value)}
            onClick={onToggle}
            disabled={disabled}
            label=""
          />
        ) : type === "multiselect" ? (
          <MultiSelectV2
            multiple={true}
            placeholder={"Select..."}
            options={options || []}
            handleSelection={onChange ?? (() => {})}
            optionValues={Array.isArray(value) ? value : []}
            _className="w-56"
            disabled={disabled}
          />
        ) : type === "select" ? (
          <Select
            options={options || []}
            placeholder={"Select..."}
            {...(getFieldProps ? getFieldProps(name) : {})}
            value={value}
            onChange={(e) => {
              if (onChange) onChange([e.target.value]);
            }}
            textGetter={textGetter}
            valueGetter={valueGetter}
            disabled={disabled}
          />
        ) : (
          <Input
            className="w-56"
            {...(getFieldProps ? getFieldProps(name) : {})}
            type={inputType}
            error={error}
            disabled={disabled}
          />
        )}
        {error && <Error text={error} />}
      </VStack>
    </HStack>
  );
};

export const SettingHeader = (props: {
  title: string;
  description: string;
}) => {
  const { title, description } = props;
  return (
    <VStack className="w-full gap-2">
      <HStack className="justify-between w-full">
        <span className="text-lg font-medium">{title}</span>
      </HStack>
      <HStack>
        <span className="text-xs">{description}</span>
      </HStack>
    </VStack>
  );
};

export const Notes = (props: {
  text?: string;
  className?: string;
  width?: string;
}) => {
  const { text, className = "", width = "w-fit" } = props;
  const classes = `rounded-md text-blue-800 bg-blue-100 p-2 mt-2 ${width} justify-start items-start ${className}`;
  return (
    <Box className={classes}>
      <HStack className="gap-4">
        <Icon
          icon="material-symbols:info-outline-rounded"
          height={24}
          width={24}
          className="text-xs font-medium text-blue-800 rounded cursor-pointer items-start inline-flex"
        />
        {text && <span className="text-sm">{text}</span>}
      </HStack>
    </Box>
  );
};
